/**
 * LEADS-OBJECT-CRM-SHELL-01 — Owner Leads list (the CRM object index).
 *
 * Turns Lead 360 from a stack of fully-expanded record cards into a COMPACT,
 * scannable Leads list — one row per lead, each row a safe reference that links
 * to the lead record detail (`/dashboard/owner/leads/:safeLeadId`). The long
 * read-model caveat that used to repeat on every expanded card is replaced by
 * ONE page-level data note (`LEADS_DATA_NOTE`); per-lead provenance stays on the
 * detail record.
 *
 * SAFETY / HONESTY (carried through from `lead-360-view` + `Lead360Panel`; this
 * surface adds no data, fetches no PHI, and invents nothing):
 *   - PHI-FREE: renders only safe fields (opaque `safeLeadId`, clinic LABEL,
 *     normalized source / UTM source, sanitized latest-activity summary,
 *     lifecycle LINKAGE statuses, created date). No patient name / phone / email
 *     / fingerprint / recording / raw payload. Never calls the contact timeline
 *     or contact vault.
 *   - ACTIVITY vs LINKAGE stay DISTINCT: the "Latest activity" column describes
 *     `events[]` only; the telephony / walk-in / revenue columns are LINKAGE
 *     wiring. A `not_available` telephony linkage renders "Not linked yet" —
 *     never "not called".
 *   - KNOWN SOURCE WINS: when the source resolves (e.g. "Google paid search")
 *     the row shows it and never also shows a competing "Unknown source"
 *     data-mode label (that label, for a literal `dataMode: 'unknown'`, stays on
 *     the detail record only).
 *   - SCOPE: owner only; ONE clinic at a time, chosen from the dashboard clinic
 *     list (no invented id, no all-clinic merge). The self-fetching wrapper owns
 *     its loading / error state so a fetch failure never breaks sibling nav.
 *
 * Styling reuses existing dashboard.css tokens (`page-header`, `table`,
 * `status-chip--*`, `empty-state`) plus a small scoped `.leads-list-*` block — no
 * new design doctrine.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { fetchOwnerDashboard, fetchOwnerLead360 } from '@/dashboard/api';
import DashboardLoadError from '@/dashboard/DashboardLoadError';
import { portalErrorStatus, safeErrorMessage } from '@/lib/portal-api';
import { useDashboardMeContext } from '@/dashboard/me-context';
import { useClinicLens } from '@/dashboard/clinic-context';
import ClinicScopeLabel from '@/dashboard/ui/ClinicScopeLabel';
import {
  buildLead360View,
  isLead360SectionVisible,
  type Lead360Input,
} from '@/dashboard/owner/lead-360-view';
import {
  compactSourceSummary,
  formatTimestampIst,
  HOP_STATUS_CHIP,
  LINK_STATUS_LABEL,
} from '@/dashboard/owner/Lead360Panel';
import {
  formatLeadRef,
  latestActivitySummaryShort,
  leadDetailHref,
  LEADS_DATA_NOTE,
  LEADS_DATA_NOTE_V2,
} from '@/dashboard/owner/leads-object-view';
import { workspaceLeadDetailHref } from '@/dashboard/leads/leads-workspace-view';

/** Default page size for the owner lead-360 read (backend hard cap is 200). */
const LEADS_LIMIT = 100;

/**
 * Compact owner initials from an operator display label (staff, never a
 * patient value) for the list owner chip -- e.g. "Asha Mehta" -> "AM",
 * "Staff #7" -> "S7". Pure; the full label rides the chip `title` for the
 * hover. Falls back to a bullet when the label is empty (never fabricated).
 */
function ownerInitials(label: string): string {
  const words = label.trim().split(/\s+/).filter((w) => w.length > 0);
  if (words.length === 0) return '—';
  if (words.length === 1) {
    const w = words[0] as string;
    return w.slice(0, 2).toUpperCase();
  }
  const first = words[0] as string;
  const last = words[words.length - 1] as string;
  return `${first[0] ?? ''}${last[0] ?? ''}`.toUpperCase();
}

interface LeadClinicOption {
  clinicId: string;
  clinicName: string;
}

/**
 * One compact lead row: a clickable safe reference + the scannable summary
 * columns. NOT an expanded Lead 360 record card — the full record lives on the
 * detail page. Linkage columns keep their DISTINCT honest statuses (the same
 * `LINK_STATUS_LABEL` / `HOP_STATUS_CHIP` vocabulary as the detail), so a
 * `not_available` telephony linkage reads "Not linked yet", never "not called".
 */
function LeadRow({
  record,
  fallbackClinicId,
  detailHref = leadDetailHref,
  showSource = true,
  showOwner = false,
  onQuickView,
}: {
  record: Lead360Input;
  fallbackClinicId: string | null;
  /**
   * Row-link builder. Defaults to the owner detail route (today's
   * behavior, byte-identical); the Leads Workspace passes its own
   * `/dashboard/leads/:safeLeadId` builder so operator roles never link
   * into the owner-gated route group.
   */
  detailHref?: (safeLeadId: string, clinicId: number | string | null) => string;
  /**
   * Whether the role-aware 'source' section is visible for this response's
   * tier (S1-P4). FALSE drops the Source cell for tiers (e.g. front-desk)
   * that receive no attribution; the column header is dropped in lockstep by
   * the parent. Defaults TRUE (owner / legacy ⇒ byte-identical).
   */
  showSource?: boolean;
  /**
   * LEAD-ASSIGNMENT-OWNERSHIP-V1: whether the Owner column is present for
   * this response (the assignment flag was on, so records carry
   * `ownerAssignment`). FALSE (the default; flag off / legacy / owner
   * lead-360 read) drops the Owner cell entirely, byte-identical. The parent
   * drops the matching header in lockstep.
   */
  showOwner?: boolean;
  /**
   * LEAD-APPOINTMENT-CRM-COHERENCE-V1 (SCOPE 7 / E7): when provided, the row
   * gains an EXPLICIT "Quick view" affordance that opens the ONE LeadQuickView
   * drawer. The row's primary click (ref + name) ALWAYS navigates to the full
   * Lead-360 page - the founder's ruling that DIRECTORY rows open the full page,
   * with the drawer kept as an explicit quick-view. Absent (owner directory) =>
   * byte-identical link behavior with no quick-view button. Triage surfaces
   * (leakage / workboard / queue-health) keep their own drawer-first rows.
   */
  onQuickView?: (record: Lead360Input) => void;
}): JSX.Element {
  const view = buildLead360View(record);
  const compactSource = compactSourceSummary(record);
  // Compact list rows use the SHORT empty-activity copy (VISUAL-POLISH-01); the
  // full wired-empty / not-wired sentences stay on the record detail.
  const latest = latestActivitySummaryShort(view);
  const linkClinicId = record.clinicId ?? fallbackClinicId;

  return (
    <tr data-testid="leads-row" data-lead-id={view.safeLeadId}>
      <td>
        {/* E7: the ref link ALWAYS navigates to the full Lead-360 page (the
            drawer moved to the explicit Quick view button in the name cell). */}
        <Link
          to={detailHref(view.safeLeadId, linkClinicId)}
          className="text-primary underline-offset-4 hover:underline"
          data-testid="lead-ref-link"
        >
          {formatLeadRef(view.safeLeadId)}
        </Link>
      </td>
      {/*
        CONTACT-REVEAL-AUDIT-01 (decision 3): the lead NAME renders plain to
        scoped operators on the list. Phone stays entirely OFF this surface
        — no value, no mask, no reveal (detail-only, decision 4).
      */}
      <td data-testid="leads-row-name">
        {onQuickView !== undefined ? (
          <span className="leads-row__name-wrap">
            {/* E7: the NAME navigates to the full page; the drawer is the
                explicit Quick view affordance beside it. */}
            <Link
              to={detailHref(view.safeLeadId, linkClinicId)}
              className="leads-row__name-link text-primary underline-offset-4 hover:underline"
              data-testid="leads-row-name-link"
            >
              {view.leadName ?? '\u2014'}
            </Link>
            <button
              type="button"
              className="leads-row__quickview-btn ac-btn ac-btn--secondary ac-btn--sm"
              data-testid="leads-row-quickview"
              onClick={() => onQuickView(record)}
              title="Quick view (drawer)"
            >
              Quick view
            </button>
          </span>
        ) : (
          view.leadName ?? '\u2014'
        )}
      </td>
      <td>{view.clinicLabel}</td>
      {showSource ? (
        <td data-testid="leads-row-source">{compactSource}</td>
      ) : null}
      {/*
        LEAD-ASSIGNMENT-OWNERSHIP-V1: the owner cell -- an owner chip (initials
        of the operator label) when assigned, else a POOL badge; an escalation
        badge when an unclaimed pool lead is over threshold. Gated on the
        page-level `showOwner` (present only when the assignment flag was on)
        AND the per-record `ownerAssignment` presence, so a record without the
        overlay renders an honest dash rather than a fabricated owner.
      */}
      {showOwner ? (
        <td data-testid="leads-row-owner">
          {record.ownerAssignment === undefined ? (
            '—'
          ) : record.ownerAssignment.assigned ? (
            <span
              className="ac-badge ac-badge--brand"
              data-testid="leads-row-owner-chip"
              title={record.ownerAssignment.ownerLabel ?? undefined}
            >
              {ownerInitials(record.ownerAssignment.ownerLabel ?? '')}
            </span>
          ) : (
            <>
              <span
                className="ac-badge ac-badge--pool"
                data-testid="leads-row-owner-pool"
                title="Not yet claimed - in the shared queue"
              >
                Unclaimed
              </span>
              {record.ownerAssignment.escalated ? (
                <span
                  className="badge badge--warning"
                  data-testid="leads-row-owner-escalated"
                >
                  Escalated
                </span>
              ) : null}
            </>
          )}
        </td>
      ) : null}
      <td data-testid="leads-row-activity">{latest}</td>
      <td data-link="callLinked" data-link-status={record.callLinked}>
        <span className={HOP_STATUS_CHIP[record.callLinked]}>
          {LINK_STATUS_LABEL[record.callLinked]}
        </span>
      </td>
      <td data-link="walkinLinked" data-link-status={record.walkinLinked}>
        <span className={HOP_STATUS_CHIP[record.walkinLinked]}>
          {LINK_STATUS_LABEL[record.walkinLinked]}
        </span>
      </td>
      <td data-link="revenueLinked" data-link-status={record.revenueLinked}>
        <span className={HOP_STATUS_CHIP[record.revenueLinked]}>
          {LINK_STATUS_LABEL[record.revenueLinked]}
        </span>
      </td>
      <td>{formatTimestampIst(view.createdAtDisplay)}</td>
    </tr>
  );
}

export interface LeadsListContentProps {
  records: readonly Lead360Input[];
  /** Selected clinic id — used as the link fallback when a record's own clinic is null. */
  clinicId?: string | number | null;
  /** Page-level data note. Defaults to today's note (byte-identical). */
  dataNote?: string;
  /** Row-link builder override (see LeadRow.detailHref). */
  detailHref?: (safeLeadId: string, clinicId: number | string | null) => string;
  /** Honest empty-state copy override. Defaults to today's copy. */
  emptyCopy?: string;
  /** STITCH-2: open the ONE LeadQuickView drawer for a row (see LeadRow). */
  onQuickView?: (record: Lead360Input) => void;
}

/**
 * Pure presentational body (no fetch, no data hooks) — rendered directly in tests
 * via react-dom/server (wrapped in a Router for the row links). Renders the
 * compact rows + the single page-level data note, or the honest empty state.
 * Shared by the owner Leads list (defaults — unchanged) and the role-scoped
 * Leads Workspace (which passes its own note / links / empty copy).
 */
export function LeadsListContent({
  records,
  clinicId = null,
  dataNote = LEADS_DATA_NOTE,
  detailHref = leadDetailHref,
  emptyCopy = 'No leads captured for this clinic yet. New enquiries will appear here.',
  onQuickView,
}: LeadsListContentProps): JSX.Element {
  const fallbackClinicId =
    clinicId === null || clinicId === '' ? null : String(clinicId);

  // Role-aware (S1-P4): every record in one response shares the caller's tier,
  // so the 'source' column is shown only when that tier receives the source
  // section. Undefined manifest (owner / legacy / empty page) ⇒ shown, so the
  // owner list renders byte-identically.
  const showSource = isLead360SectionVisible(records[0]?.visibleSections, 'source');

  // LEAD-ASSIGNMENT-OWNERSHIP-V1: the Owner column is present only when the
  // response carries the ownership overlay (the assignment flag was on for
  // this read). Absent (flag off / owner lead-360 read / empty page) => no
  // column, byte-identical to today.
  const showOwner = records[0]?.ownerAssignment !== undefined;

  return (
    <section className="clinic-table" aria-label="Leads">
      <p className="panel-caveat leads-list__note" data-testid="leads-data-note">
        {dataNote}
      </p>
      {records.length > 0 ? (
        <table className="table" data-testid="leads-table">
          <thead>
            <tr>
              <th>Lead</th>
              <th>Name</th>
              <th>Clinic</th>
              {showSource ? <th>Source</th> : null}
              {showOwner ? <th>Owner</th> : null}
              <th>Latest activity</th>
              <th>Telephony (Office24x7)</th>
              <th>Walk-in</th>
              <th>Revenue</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {records.map((r) => (
              <LeadRow
                key={r.safeLeadId}
                record={r}
                fallbackClinicId={fallbackClinicId}
                detailHref={detailHref}
                showSource={showSource}
                showOwner={showOwner}
                onQuickView={onQuickView}
              />
            ))}
          </tbody>
        </table>
      ) : (
        <div className="empty-state" data-testid="leads-empty">
          {emptyCopy}
        </div>
      )}
    </section>
  );
}

/**
 * Flag-gated accurate-security header copy
 * (LEADS-WORKSPACE-ROLE-SCOPED-01 inherited item — replaces the stale
 * "PHI-free" claim once the workspace flag is on; flag-off keeps
 * today's copy byte-identically).
 */
export const LEADS_HEADER_SUB_V2 =
  'Lead records for one clinic at a time, scoped to your verified access. ' +
  'Open a lead for its activity timeline, integration linkage, and the ' +
  'audited contact reveal.';

/**
 * Self-fetching, fault-isolated Leads list. Loads the owner clinic list once
 * (reusing the dashboard read — same source as the Lead 360 panel), scopes to ONE
 * clinic (default = first), and reads its PHI-safe lead records from the existing
 * `GET /api/owner/lead-360` endpoint. No new backend route, no PHI.
 */
export default function LeadsListPage(): JSX.Element {
  const meCtx = useDashboardMeContext();
  // Server-computed signal: present ONLY when the backend flag is on
  // and the role is in the workspace audience. Absence = today's UI.
  const polished = meCtx?.me?.leadsWorkspaceEnabled === true;
  const clinicCtx = useClinicLens();
  const lensClinicId = clinicCtx?.selectedClinicId ?? null;
  const [clinics, setClinics] = useState<LeadClinicOption[]>([]);
  const [selectedClinicId, setSelectedClinicId] = useState<string>('');
  const [records, setRecords] = useState<Lead360Input[] | null>(null);
  const [loadingClinics, setLoadingClinics] = useState(true);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [clinicError, setClinicError] = useState<string | null>(null);
  const [clinicErrorStatus, setClinicErrorStatus] = useState<number | undefined>(
    undefined,
  );
  const [leadError, setLeadError] = useState<string | null>(null);

  // Load the owner clinic list once; scope defaults to the first clinic.
  useEffect(() => {
    let cancelled = false;
    setLoadingClinics(true);
    setClinicError(null);
    setClinicErrorStatus(undefined);
    void fetchOwnerDashboard()
      .then(({ clinics: rows }) => {
        if (cancelled) return;
        const options = rows
          .filter((c) => c.clinicId.trim() !== '')
          .map((c) => ({ clinicId: c.clinicId, clinicName: c.clinicName }));
        setClinics(options);
        // THE LENS OWNS "WHERE AM I" (119-CLINIC-OPERATING-SPINE-V1, Phase 2).
        // Leads are FENCED (patients are portable, leads are not), so working
        // the lead list is operational work, not branch comparison -- it reads
        // the lens. The lens carries numeric ids; this owner read carries
        // strings, so the pick is matched by value. With no lens pick ("All
        // clinics") the first clinic remains the default exactly as before,
        // and the surface NAMES it -- an unnamed arbitrary branch is the defect,
        // a named default is not.
        const lensPick =
          lensClinicId !== null
            ? (options.find((o) => Number(o.clinicId) === lensClinicId)?.clinicId ??
              null)
            : null;
        setSelectedClinicId(lensPick ?? options[0]?.clinicId ?? '');
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setClinicError(safeErrorMessage(e, 'Something went wrong. Retry.'));
          setClinicErrorStatus(portalErrorStatus(e));
          setClinics([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingClinics(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Load the selected clinic's lead records.
  useEffect(() => {
    if (selectedClinicId === '') return undefined;
    let cancelled = false;
    setLoadingLeads(true);
    setLeadError(null);
    void fetchOwnerLead360(selectedClinicId, LEADS_LIMIT)
      .then((rows) => {
        if (!cancelled) setRecords(rows);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setLeadError(safeErrorMessage(e, 'Failed to load leads'));
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingLeads(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedClinicId]);

  return (
    <section className="leads-list">
      <div className="page-header">
        <h1>Leads</h1>
        <p className="page-header__sub">
          {polished
            ? LEADS_HEADER_SUB_V2
            : 'A PHI-free lead record for one clinic at a time. Open a lead to see its full activity timeline and integration linkage.'}
        </p>
      </div>

      {clinicError ? (
        <div className="mb-4">
          <DashboardLoadError
            message={clinicError}
            {...(clinicErrorStatus !== undefined
              ? { status: clinicErrorStatus }
              : {})}
          />
        </div>
      ) : null}

      {loadingClinics ? (
        <div className="loading">Loading…</div>
      ) : clinics.length === 0 && !clinicError ? (
        <div className="empty-state" data-testid="leads-no-clinic">
          No clinic is available to scope leads yet.
        </div>
      ) : clinics.length > 0 ? (
        <>
          {/* 119-CLINIC-OPERATING-SPINE-V1 (Phase 2): this page's OWN clinic
              select is gone -- a second control for a value the header lens
              owns, which is why this surface could disagree with every other
              one. The clinic is NAMED through THE ONE scope primitive. */}
          <p className="clinic-table__subtitle">
            <ClinicScopeLabel
              label={
                clinics.find((c) => c.clinicId === selectedClinicId)?.clinicName ??
                clinics[0]?.clinicName ??
                ''
              }
              testId="leads-clinic-select"
            />
          </p>

          {loadingLeads ? (
            <div className="loading">Loading…</div>
          ) : leadError !== null ? (
            <p data-testid="leads-error">Leads unavailable. {leadError}</p>
          ) : (
            <LeadsListContent
              records={records ?? []}
              clinicId={selectedClinicId}
              {...(polished
                ? {
                    dataNote: LEADS_DATA_NOTE_V2,
                    // LEAD360-DEDUP-V1 (G4): when the canonical Leads Workspace
                    // surface is live, owner list rows link to the ONE canonical
                    // detail route; flag-off keeps today's owner-route href
                    // byte-identically (LeadsListContent's default detailHref).
                    detailHref: workspaceLeadDetailHref,
                  }
                : {})}
            />
          )}
        </>
      ) : null}
    </section>
  );
}
